import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShareLinkBlock } from "@/components/ShareLinkBlock";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, Send, Rocket, Share2,
} from "lucide-react";

const CHECK_ICON = { approved: CheckCircle2, warning: AlertTriangle, error: XCircle };
const CHECK_COLOR = { approved: "text-[#10B981]", warning: "text-[#F59E0B]", error: "text-[#EF4444]" };
const CHECK_LABEL = { technical: "Datos técnicos", seo: "SEO", brand: "Marca", legal: "Legal" };

export default function GuideDetailSheet({ guide, open, onOpenChange, onChanged }) {
  const { activeTenant } = useAuth();
  const [busy, setBusy] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [share, setShare] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
      setShare(null);
      setLeadId("");
    }
  }, [open, guide?.id]);

  if (!guide) return null;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      const res = await fn();
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo completar la acción");
    } finally {
      setBusy(null);
    }
  };

  const sendGuide = async () => {
    setBusy("send");
    try {
      const res = await api.post(`/guides/${guide.id}/send`, leadId ? { lead_id: leadId } : {});
      setShare(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el link");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="guide-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-left">{guide.title || guide.topic}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {guide.error_message && (
            <div className="bg-[#EF4444]/10 text-[#EF4444] text-sm p-3 rounded-md" data-testid="guide-error-message">
              Falló la generación: {guide.error_message}
            </div>
          )}
          {guide.requires_review && (
            <div className="bg-[#F59E0B]/10 text-[#92400E] text-sm p-3 rounded-md flex gap-2" data-testid="guide-requires-review-banner">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>No hay documentos de la base de conocimiento que cubran esta jurisdicción/código postal.
              La guía deja los datos técnicos en blanco y requiere revisión manual antes de publicarse.</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 text-xs text-[#71717A]">
            <Badge variant="outline" className="rounded-sm">{guide.guide_type}</Badge>
            <Badge variant="outline" className="rounded-sm">{guide.audience}</Badge>
            {guide.state && <Badge variant="outline" className="rounded-sm">{guide.state}{guide.zip_code ? ` ${guide.zip_code}` : ""}</Badge>}
            <Badge variant="outline" className="rounded-sm font-mono">/g/{guide.slug}</Badge>
          </div>

          {guide.body && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Contenido (en inglés)</h4>
              <div
                className="prose prose-sm max-w-none border border-[#E4E4E7] rounded-md p-4 max-h-72 overflow-y-auto bg-[#FAFAFA]"
                data-testid="guide-body-preview"
                dangerouslySetInnerHTML={{ __html: guide.body }}
              />
            </div>
          )}

          {(guide.technical_citations || []).length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Citas técnicas</h4>
              <div className="space-y-2">
                {guide.technical_citations.map((c, i) => (
                  <div key={i} className="text-xs border border-[#E4E4E7] rounded-md p-2.5" data-testid={`guide-citation-${i}`}>
                    <p className="font-medium">{c.claim}</p>
                    <p className="text-[#71717A] mt-1">Fuente: {c.document_title} (fragmento #{c.chunk_index})</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(guide.review_checks || []).length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Revisión</h4>
              <div className="space-y-2">
                {guide.review_checks.map((c, i) => {
                  const Icon = CHECK_ICON[c.status];
                  return (
                    <div key={i} className="flex gap-2 text-xs" data-testid={`guide-check-${c.check_type}`}>
                      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${CHECK_COLOR[c.status]}`} />
                      <div><span className="font-medium">{CHECK_LABEL[c.check_type]}:</span> {c.message}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Separator />

          <div className="flex flex-wrap gap-2" data-testid="guide-actions">
            {guide.status === "draft" && !guide.error_message && (
              <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "review"}
                onClick={() => run("review", () => api.post(`/guides/${guide.id}/submit-review`))} data-testid="guide-submit-review-button">
                {busy === "review" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Enviar a revisión
              </Button>
            )}
            {guide.status === "pending_review" && (
              <Button size="sm" className="bg-[#10B981] hover:bg-[#0F9D6F] rounded-md" disabled={busy === "approve"}
                onClick={() => run("approve", () => api.post(`/guides/${guide.id}/approve`))} data-testid="guide-approve-button">
                {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Aprobar
              </Button>
            )}
            {guide.status === "approved" && (
              <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" disabled={busy === "publish"}
                onClick={() => run("publish", () => api.post(`/guides/${guide.id}/publish`))} data-testid="guide-publish-button">
                {busy === "publish" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Rocket className="h-3.5 w-3.5 mr-1" />} Publicar
              </Button>
            )}
          </div>

          {guide.status === "published" && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2 flex items-center gap-1">
                <Share2 className="h-3.5 w-3.5" /> Enviar guía
              </h4>
              <div className="flex gap-2">
                <Select value={leadId} onValueChange={setLeadId}>
                  <SelectTrigger data-testid="guide-send-lead-select" className="flex-1"><SelectValue placeholder="(opcional) elegí un lead / contacto" /></SelectTrigger>
                  <SelectContent>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.phone}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  size="sm" onClick={sendGuide} disabled={busy === "send" || activeTenant?.status === "suspended"}
                  title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
                  className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="guide-send-button"
                >
                  {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
              {share && <div className="mt-3"><ShareLinkBlock share={share} message={`Here's the guide you asked about: ${share.short_url}`} /></div>}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
